import axios from "axios";
import qs from "qs";
import Ajv from "ajv";
import { Message } from "element-ui";
import { pickBy } from "lodash-es";
import store from "@/store/index.js";
import { cookieCacheGet } from "@/tool/cache.js";
import { errorLog } from "@/tool/helper.js";
import router from "@/router/index.js";
import apiBaseURL from "@/utils/baseUrl.js";

console.log("request.js", process.env.NODE_ENV);

const generateFormKey = (key0, key1) => {
  return !key0 ? key1 : `${key0}[${key1}]`;
};
// 生成 formData 数据
const generateFormData = (target, key, formdata) => {
  const formData = formdata || new FormData();
  if (Object.prototype.toString.call(target) === "[object Date]") {
    // 时间直接添加
    formData.append(key, target.toLocaleString());
  } else if (Array.isArray(target)) {
    // 数组循环添加
    target.forEach((item, index) => {
      generateFormData(item, generateFormKey(key, index), formData);
    });
  } else if (Object.prototype.toString.call(target) === "[object Object]") {
    // 非文件的常规对象
    Object.keys(target).forEach((k) => {
      generateFormData(target[k], generateFormKey(key, k), formData);
    });
  } else {
    formData.append(key, target);
  }
  return formData;
};
/**
 * 去除 falsy 值，接口那边需要过滤空参数
 */
const removeFalsy = (obj) => {
  return pickBy(obj, (value) => ![undefined, null, ""].includes(value));
};
const ajv = new Ajv();
ajv.addKeyword("tag", {});
const axiosInstance = axios.create({
  baseURL: apiBaseURL,
  withCredentials: true,
  // #config里面有这个transformRquest，这个选项会在发送参数前进行处理。
  // #这时候我们通过Qs.stringify转换为表单查询参数
  transformRequest: [
    function transformRequest(data, headers) {
      if (headers["Content-Type"] === "application/json") {
        return JSON.stringify(data);
      }
      if (headers["Content-Type"] === "multipart/form-data") {
        return data;
      }
      const pickData = removeFalsy(data);
      return qs.stringify(pickData);
    },
  ],
  // #设置Content - Type
  headers: {
    "Content-Type": "application/x-www-form-urlencoded",
  },
});
axiosInstance.interceptors.request.use(
  (config) => {
    if (config.data instanceof FormData) {
      return config;
    }
    cookieCacheGet("deskNo");
    // eslint-disable-next-line no-param-reassign
    config.data = {
      ...config.data,
      is_pc: 1,
    };

    return config;
  },
  () => {
    store.commit("SET_LOADING", false);
  }
);
// 拦截请求返回数据，如果返回的code是401则删除token并跳转到login
axiosInstance.interceptors.response.use(
  (response) => {
    store.commit("SET_LOADING", false);

    const { data: responseData } = response;
    let { code } = responseData;
    // 此处兼容后端返回的二进制数据类型
    if (!code) {
      return response;
    }
    // 转一下code的数据类型
    code = parseInt(code, 10);
    switch (code) {
      case 201:
        // 登陆过期或者未登录 返回登陆页
        router.replace("/");
        return response;
      case 200:
        // 默认把response对象返回 兼容之前的代码
        return response;
      default:
        // Message({
        //   message: msg,
        //   type: "error",
        //   duration: 2 * 1000,
        // });
        return response;
    }
  },
  (error) => {
    store.commit("SET_LOADING", false);
    const { status } = error.response;
    let message = "";
    switch (status) {
      case 400:
        message = "请求错误";
        break;
      case 401:
        message = "未授权，请登录";
        break;
      case 403:
        message = "拒绝访问";
        break;
      case 404:
        message = `请求地址出错: ${error.response.config.url}`;
        break;
      case 408:
        message = "请求超时";
        break;
      case 500:
        message = "服务器内部错误";
        break;
      case 501:
        message = "服务未实现";
        break;
      case 502:
        message = "网关错误";
        break;
      case 503:
        message = "服务不可用";
        break;
      case 504:
        message = "网关超时";
        break;
      case 505:
        message = "HTTP版本不受支持";
        break;
      default:
        message = "未知的错误";
        break;
    }
    // eslint-disable-next-line no-param-reassign
    error.message = message;
    errorLog(error);
    return Promise.reject(error);
  }
);

export default function request(
  payload,
  options = {
    useLoading: true,
    useDataField: true,
    useToast: true,
    isMock: false,
    useTruly: false,
  }
) {
  // 增加默认值
  const {
    useLoading = true,
    useDataField = true,
    useToast = true,
    isMock = false,
    useTruly = false,
  } = options;
  let { path } = payload;
  if (payload.requestBodyType === "query") {
    const [url, query] = payload.path.split("?");
    const params = qs.parse(query);
    const pickParams = removeFalsy(params);
    path = isMock
      ? `${payload.mockUrl}${url}?${qs.stringify(pickParams)}`
      : `${url}?${qs.stringify(pickParams)}`;
  }
  if (useLoading) {
    store.commit("SET_LOADING", true);
  }
  let params = {
    method: payload.method,
    url: path,
    data: useTruly ? removeFalsy(payload.data) : payload.data,
    headers: { "Content-Type": "application/json" },
  };
  if (payload.requestBodyType === "form") {
    const formData = generateFormData(payload.data);
    params = {
      method: payload.method,
      url: isMock ? `${payload.mockUrl}${payload.path}` : payload.path,
      data: formData,
      headers: { "Content-Type": "multipart/form-data" },
    };
  }
  return axiosInstance(params)
    .then((res) => {
      const { code, data, msg } = res.data;
      // 此处兼容后端返回的二进制数据类型
      if (!code) {
        return res;
      }
      if (String(code) === "200") {
        return useDataField ? data : res;
      }
      if (useToast) {
        Message({
          message: msg,
          type: "error",
          duration: 5 * 1000,
        });
      }
      // 业务对象 {code: '', data: {}, msg: ''}
      return Promise.reject(res.data);
    })
    .catch((e) => {
      // 错误对象{name: '', message: ''}
      if (!e.msg) {
        // 判断非业务对象才报错，否则会和上面68行的Message.error重复报
        Message.error(e.message);
      }
      return Promise.reject(e);
    });
}



// WEBPACK FOOTER //
// ./src/openapi/request.js